import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { ErrorHandlerService } from '../../core/services/error-handler.service';
import { ModalPopupMessages, StatusCodes } from '../utility/constants';

export const DefaultErrorHandlerConfig = {
  /** True when the caller renders this error itself (e.g. a modal); no toast, still re-thrown. */
  skipToast: (() => false) as (errorResponse: HttpErrorResponse) => boolean,
  showRedErrorToast: (errorResponse: HttpErrorResponse) =>
    errorResponse.status === 400,
  showWarningErrorToast: (error: HttpErrorResponse) =>
    error.error?.code === 'ASSOCIATION_EXISTS' ||
    !!error.error?.message?.startsWith('association:'),
  extractError: (error: HttpErrorResponse) => {
    const fields = error.error?.details?.fields as
      | { field: string; message: string }[]
      | undefined;
    if (Array.isArray(fields) && fields.length > 0) {
      return fields.map(formatValidationFieldMessage).join('; ');
    }
    if (error.error?.code) {
      return error.error.message;
    }
    const splitError = error.error.message.split(':');
    if (splitError.length > 1) {
      splitError.shift();
      return splitError.join(':');
    }

    return error.error.message;
  },
} as const;

function formatValidationFieldMessage(fieldError: {
  field: string;
  message: string;
}): string {
  const leaf =
    fieldError.field
      .split('.')
      .filter(segment => segment !== 'body' && !/^\d+$/.test(segment))
      .pop() ?? fieldError.field;
  if (fieldError.message === 'Required') {
    return `${leaf} is required`;
  }
  if (fieldError.message === 'Expected string, received null') {
    return `${leaf} cannot be empty`;
  }
  return `${leaf}: ${fieldError.message}`;
}

/**
 * Handle the status display in rxjs call stream.
 *
 * EXAMPLE USAGE:
 * @example With default values
 *
 * ```
 * errorHandler = handleErrorMessage();
 * call.pipe(errorHandler())
 * ```
 *
 */
export function handleErrorMessage() {
  const errorHandlerService = inject(ErrorHandlerService);
  return (customConfig?: Partial<typeof DefaultErrorHandlerConfig>) => {
    const config = { ...DefaultErrorHandlerConfig, ...customConfig };
    const error = (error: HttpErrorResponse) => throwError(() => error);

    return function <T>(source: Observable<T>) {
      return source.pipe(
        tap((response: any) => {
          const code = response.statusCode ?? response.status;
          if (typeof response === 'string') {
            return;
          }
          if (
            ![StatusCodes.Success, StatusCodes.SuccessfulRequest].includes(code)
          ) {
            errorHandlerService.handleApiError(response);
          }
        }),
        catchError((response: HttpErrorResponse) => {
          if (config.skipToast(response)) {
            return error(response);
          }
          if (config.showWarningErrorToast(response)) {
            errorHandlerService.handleApiError(
              {
                statusCode: StatusCodes.BadRequest,
                message: config.extractError(response),
              },
              ModalPopupMessages.Warning
            );
            return error(response);
          }
          if (config.showRedErrorToast(response)) {
            errorHandlerService.handleApiError({
              statusCode: StatusCodes.BadRequest,
              message: config.extractError(response),
            });
          } else if (response.error?.code && response.error?.message) {
            errorHandlerService.handleApiError({
              statusCode: response.status,
              message: response.error.message,
            });
          } else {
            errorHandlerService.handleError(response);
          }

          return error(response);
        })
      );
    };
  };
}
